import { Controller } from "@hotwired/stimulus"
import { comparePeriod, periodLabel } from "../lib/ai_report_period.js"

// AiReports::Period と同じ範囲(サーバ側の検証が正。ここは入力中の目安)
const MIN_DAYS = 7
const MAX_DAYS = 366
const DAY_MS = 24 * 60 * 60 * 1000

// "YYYY-MM-DD" を UTC の日付として扱う(タイムゾーンで日がずれないように)
function parseDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "")
  return match ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))) : null
}

// 既存のテストが controller から import しているので再輸出する
export { periodLabel }

// カスタム期間の表示文言。{ text, error }(error が true なら text は理由。文言は AiReports::Period のエラーと同じ)
export function customPeriodText(fromValue, toValue, yesterdayValue) {
  const from = parseDate(fromValue)
  const to = parseDate(toValue)
  if (!from || !to) return { text: "カレンダーで期間を選んでください", error: false }

  const yesterday = parseDate(yesterdayValue)
  const days = Math.round((to - from) / DAY_MS) + 1
  if (from > to) return { text: "開始日は終了日より前にしてください", error: true }
  if (yesterday && to > yesterday) return { text: "期間は前日までで選んでください", error: true }
  if (days < MIN_DAYS) return { text: `期間は${MIN_DAYS}日以上で選んでください`, error: true }
  if (days > MAX_DAYS) return { text: `期間は${MAX_DAYS}日以内で選んでください`, error: true }

  const cmp = comparePeriod(from, to)
  return { text: `${periodLabel(from, to)}(比較 ${periodLabel(cmp.from, cmp.to)})`, error: false }
}

// AIレポート作成モーダル。プリセットのチップで期間を切り替え、カスタムは date-range-calendar の値を hidden に写す
export default class extends Controller {
  static targets = ["modal", "box", "preset", "fromDate", "toDate", "custom", "periodLabel", "chip", "error", "focusNote", "focusNoteCount"]
  static values = { labels: Object, yesterday: String }

  open(event) {
    event.preventDefault()
    this.reset()
    this.modalTarget.showModal()
  }

  // 開き直したときに前回の入力を残さない: 作成エラーを消し、カスタム期間を選んでいたら前月に戻し、重点メモを空にする
  reset() {
    this.errorTarget.textContent = ""
    if (this.presetTarget.value === "custom") {
      const lastMonth = this.chipTargets.find((chip) => chip.dataset.preset === "last_month")
      if (lastMonth) this.choose({ currentTarget: lastMonth })
    }
    this.focusNoteTarget.value = ""
    this.countFocusNote()
  }

  // キャンセルと turbo:before-cache(開いたままのダイアログをキャッシュに残さない)
  close() {
    if (this.modalTarget.open) this.modalTarget.close()
  }

  choose(event) {
    this.errorTarget.textContent = "" // 前回の作成エラーは条件を変えたら消す
    const preset = event.currentTarget.dataset.preset
    const custom = preset === "custom"
    this.presetTarget.value = preset
    this.chipTargets.forEach((chip) => {
      const active = chip.dataset.preset === preset
      chip.classList.toggle("btn-primary", active)
      chip.setAttribute("aria-pressed", String(active))
    })
    this.customTarget.hidden = !custom
    // カレンダーを出すときは2か月を横に並べられる幅にする(md 以上。クラスはビュー側)
    if (this.hasBoxTarget) this.boxTarget.toggleAttribute("data-wide", custom)
    if (custom) {
      this.syncCustom()
    } else {
      this.fromDateTarget.value = ""
      this.toDateTarget.value = ""
      this.setPeriodLabel(this.labelsValue[preset] || "", false)
    }
  }

  // date-range-calendar の選択値("YYYY-MM-DD/YYYY-MM-DD")を hidden に写し、期間の表示を更新する
  syncCustom() {
    this.errorTarget.textContent = ""
    const [from, to] = this.calendarValue().split("/")
    this.fromDateTarget.value = from || ""
    this.toDateTarget.value = to || ""
    const { text, error } = customPeriodText(from, to, this.yesterdayValue)
    this.setPeriodLabel(text, error)
  }

  // カスタム選択中だけ写す。2か所から呼ぶ:
  //  - カレンダーの change(date-range-calendar は接続時にも change を投げるので、プリセット選択中は表示を変えない)
  //  - フォーム自身の submit(Turbo は document の submit(バブリング)で FormData を作るので、それより先に走る)
  syncIfCustom() {
    if (this.presetTarget.value === "custom") this.syncCustom()
  }

  countFocusNote() {
    this.focusNoteCountTarget.textContent = `${this.focusNoteTarget.value.length}/${this.focusNoteTarget.maxLength}`
  }

  calendarValue() {
    const calendarElement = this.customTarget.querySelector('[data-controller~="date-range-calendar"]')
    const controller = calendarElement && this.application.getControllerForElementAndIdentifier(calendarElement, "date-range-calendar")
    return controller?.elementTarget?.value || ""
  }

  setPeriodLabel(text, error) {
    this.periodLabelTarget.textContent = text
    this.periodLabelTarget.classList.toggle("text-error", error)
    this.periodLabelTarget.classList.toggle("text-base-content/70", !error)
  }
}
